import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Department } from '@estimator/shared';

interface RoleInfo {
  id: string;
  name: string;
  slug: string;
  is_system: boolean;
  department: Department | null;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role_id: string | null;
  role: RoleInfo | null;
  account_type: string;
  must_change_password: boolean;
  permissions: string[];
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;

  // Actions
  setToken: (accessToken: string) => void;
  setUser: (user: AuthUser) => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,

      setToken: (accessToken) => {
        set({ accessToken });
      },

      setUser: (user) => {
        set({ user });
      },

      login: async (email, password) => {
        const { apiClient } = await import('@/api/client');
        const response = await apiClient.post<{
          access_token: string;
          user: { id: string; email: string };
        }>('/auth/login', { email, password });

        const { access_token } = response.data;
        set({ accessToken: access_token });

        // Fetch full profile
        const profileRes = await apiClient.get<AuthUser>('/auth/me', {
          headers: { Authorization: `Bearer ${access_token}` },
        });
        set({ user: profileRes.data });
      },

      signup: async (email, password, name) => {
        const { apiClient } = await import('@/api/client');
        await apiClient.post<{ message: string }>('/auth/register', {
          email,
          password,
          name,
        });
        // No token returned — user must verify email first
      },

      logout: () => {
        set({ accessToken: null, user: null });
      },
    }),
    {
      name: 'estimator-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
      }),
    },
  ),
);

/** Selector to check if user is authenticated */
export const selectIsAuthenticated = (state: AuthState) =>
  state.accessToken !== null && state.user !== null;
